/*
  CAMADA DE CONTEUDO.

  Fato confirmado vira texto. Fato ainda em aberto vira `null`, e a pagina
  desenha um buraco visivel no lugar. Nada de "a definir" escrito a mao, nada
  de valor plausivel inventado para preencher.

  Fonte da verdade: 05-site/recebidos/2026-10-19-roteiro-de-ajuste-hcrm.docx,
  aprovado pelo grupo, mais 01-projeto/dados-do-curso.md. Numero que for para
  a pagina sai de la, nunca de nota de reuniao.

  UM VALOR, UMA FONTE. Ate 19/10 o acesso as gravacoes estava escrito a mao em
  tres arquivos diferentes, e quando o grupo mudou de tres para doze meses a
  pagina passou a publicar os dois numeros ao mesmo tempo. Por isso cada dado
  que aparece em mais de um lugar agora e um campo so, interpolado por quem
  precisa. Se voce for escrever um numero dentro de uma frase, pare e crie o
  campo.
*/

/*
  ESPACO QUE NAO QUEBRA. Numero e unidade nao podem cair em linhas
  diferentes: "22" no fim de uma linha e "horas" no comeco da outra le como
  erro. Por isso os valores abaixo usam \u00a0 no lugar do espaco comum
  entre o numero e o que ele conta. Ao escrever uma frase nova com numero,
  faca o mesmo.
*/
export const curso = {
  /* Marca curta, usada no cabecalho e no rodape. */
  marca: 'HCRM',
  /* Nome do curso, fechado em 22/08/2026. */
  nome: 'Alta Confiabilidade na Saúde',
  formato: 'Imersão HCRM',
  /* A frase de abertura. O roteiro de 19/10 manda manter integralmente. */
  chamada: 'O erro é humano. A confiabilidade é construída.',
  /* A promessa vira lista na abertura, por pedido de 20/10: a frase e o
     cabecalho, as quatro competencias sao os itens. */
  promessa: 'Desenvolva as competências que sustentam equipes de alta confiabilidade:',
  competenciasDaPromessa: [
    'Comunicação',
    'Liderança',
    'Consciência situacional',
    'Tomada de decisão sob pressão',
  ],
  /* Texto da Kelly, 20/10: deixa explicito que e para graduados. */
  publico:
    'Imersão para profissionais graduados da área da saúde, incluindo lideranças, que atuam em ambientes complexos, críticos e de alta pressão.',

  data: '17 e 18 de abril de 2027',
  diasDaSemana: 'sábado e domingo',
  horarioSabado: 'das 7h30 às 18h',
  horarioDomingo: 'das 7h30 às 13h',
  parceiro: 'HCX',
  instituicao: 'HCFMUSP',
  /* Como o roteiro nomeia o conjunto, quando fala da inscricao e do local. */
  parceiroCompleto: 'HCX\u00a0FMUSP',
  turma: '12\u00a0a\u00a024\u00a0participantes',
  preco: 'R$\u00a03.000,00',
  /*
    22 horas desde 20/10, pela revisao da Kelly. Ela nao detalhou a divisao,
    mas manteve "14 horas presenciais" no bloco do fim de semana, entao a
    preparacao passou de 6 para 8 por subtracao. Se a divisao real for outra,
    e so mudar horasOnline.
  */
  cargaHoraria: '22\u00a0horas',
  horasOnline: 8,
  horasPresenciais: 14,
  /* Mudou de Instituto HCX para a FMUSP na reuniao de 14/10. */
  local: 'Faculdade de Medicina da Universidade de São Paulo',
  localCurto: 'FMUSP',
  cenarios: 6,
  videoaulas: 12,
  duracaoAula: '30\u00a0minutos',
  /* Ampliado de 6 para 12 meses na reuniao de 14/10. */
  acesso: '12\u00a0meses',
  base: 'TeamSTEPPS 3.0',
} as const;

/*
  A linha de fatos da abertura.

  Montada aqui, e nao no JSX, porque ela cola preposicao em `local`. Enquanto
  o local foi "Instituto HCX" o componente escrevia "no ${local}" e funcionava
  por sorte; com "Faculdade" a frase passaria a sair errada. Gramatica mora
  junto com as palavras.
*/
export const fatosDaAbertura = `${curso.data} · ${curso.parceiroCompleto} · ${curso.cargaHoraria} · Formato híbrido`;

export interface LinhaDaFicha {
  rotulo: string;
  valor: string | null;
}

/** O que quem decide pela equipe procura primeiro, num lugar so. */
export const fichaTecnica: LinhaDaFicha[] = [
  { rotulo: 'Quando', valor: `${curso.data}, ${curso.diasDaSemana}` },
  {
    rotulo: 'Horário',
    valor: `Sábado, ${curso.horarioSabado}. Domingo, ${curso.horarioDomingo}`,
  },
  { rotulo: 'Onde', valor: `${curso.local} (${curso.localCurto})` },
  {
    rotulo: 'Formato',
    valor: 'Híbrido: videoaulas, dois encontros ao vivo e um fim de semana presencial',
  },
  {
    rotulo: 'Carga horária',
    valor: `${curso.cargaHoraria}: ${curso.horasOnline}\u00a0horas de preparação e ${curso.horasPresenciais}\u00a0horas de experiência prática presencial`,
  },
  { rotulo: 'Turma', valor: curso.turma },
  { rotulo: 'Investimento', valor: `${curso.preco} por participante` },
  {
    rotulo: 'Certificação',
    valor: 'Curso de atualização, conforme critérios de frequência e aproveitamento',
  },
  {
    rotulo: 'Inscrição',
    valor: `Realizada pelo ${curso.parceiroCompleto} quando as inscrições forem abertas`,
  },
];

/**
 * Por que esta imersao e diferente.
 *
 * E a prova que existe hoje, no lugar do depoimento que ainda nao existe.
 *
 * A chancela nomeia a professora por decisao do grupo em 19/10. A reuniao de
 * 14/10 registrou que o uso do nome dependia de confirmacao formal; a decisao
 * de publicar foi tomada depois disso, com ciencia da pendencia.
 */
export const provas: { titulo: string; texto: string }[] = [
  {
    titulo: 'Chancela institucional',
    texto: `Formação do ${curso.instituicao}, pela disciplina de Emergência, com anuência da Professora Ludhmila Abrahão Hajjar.`,
  },
  {
    titulo: 'Base atualizada',
    texto: `Conteúdo baseado no ${curso.base} e em referenciais contemporâneos de fatores humanos e segurança.`,
  },
  {
    titulo: 'Aprendizagem pela experiência',
    texto: `${curso.cenarios}\u00a0cenários inspirados em situações reais, seguidos de observação estruturada e debriefing.`,
  },
  {
    titulo: 'Uma linguagem comum',
    texto:
      'Quem lidera e quem atua na linha de frente aprendem as mesmas ferramentas e compartilham a mesma linguagem de segurança.',
  },
];

/** Descontos pela politica do EEP, aplicada pelo HCX. */
export const descontos: { quem: string; quanto: string }[] = [
  /* Graduacao da FMUSP saiu em 20/10: o curso e para profissionais graduados. */
  { quem: 'Colaboradores do Complexo HCFMUSP, FFM, FZ e SES', quanto: '10%' },
  { quem: 'Residentes, estagiários e pós-graduandos da FMUSP', quanto: '10%' },
  { quem: 'Alunos e ex-alunos do EEP', quanto: '5%' },
  { quem: 'Pagamento à vista', quanto: '5%' },
];

/** Confirmado em 20/10. Fecha a duvida que estava marcada como pendente. */
export const regraDosDescontos = 'Os descontos não são acumulativos.';

/**
 * O que esta incluido.
 *
 * Virou ficha, e nao lista, porque o roteiro agrupou por etapa da jornada em
 * vez de enumerar itens soltos. Mesmo molde do resumo.
 */
export const incluso: LinhaDaFicha[] = [
  {
    rotulo: 'Preparação',
    valor: `${curso.videoaulas}\u00a0videoaulas, materiais de apoio, e-book e acesso por ${curso.acesso}`,
  },
  { rotulo: 'Interação', valor: 'Dois encontros ao vivo e fórum de discussão' },
  {
    rotulo: 'Imersão presencial',
    valor: `${curso.horasPresenciais}\u00a0horas, ${curso.cenarios}\u00a0cenários, pequenos grupos, observação estruturada e debriefing`,
  },
  {
    rotulo: 'Conclusão',
    valor: 'Certificado de curso de atualização, materiais e ferramentas para aplicação',
  },
];
